import { useProgramData } from "@features/program/useProgramData"
import { getLastViewedFilms } from "@features/tracking/film-history"
import { BrowseFilmTileFragment } from "@generated-types"
import { useAppData } from "eddev/hooks"
import { useHydrating } from "eddev/routing"
import { ReactNode, useEffect, useMemo, useState } from "react"
import { SFFRagDocumentTypeName } from "../../server/search/rag"
import { getSimpleSearchResult } from "./cards/StandardSearchResult"
import { SearchDocumentResult } from "./search-system"
import { SEARCH_WIDGETS, SearchWidgetInstance } from "./search-widgets"
import { useSearch } from "./useSearchInput"

type Args = {
  enabled?: boolean
  isMobile?: boolean
  isQuickSearch?: boolean
  types?: SFFRagDocumentTypeName[]
}

export type RecommendationGroup = {
  heading: string
  items: RecommendedLink[]
}

export type RecommendedLink =
  | {
      type: "search"
      label: string
      term: string
    }
  | {
      type: "link"
      label: string
      link: string
      external?: boolean
    }
export type SearchTab = {
  key: string
  types?: SFFRagDocumentTypeName[]
  label: string
  enabled: boolean
}

export type SearchResult =
  | {
      kind: "document"
      key: string
      score: number
      document: SearchDocumentResult
      node: ReactNode
    }
  | {
      kind: "widget"
      key: string
      score: number
      widget: SearchWidgetInstance
    }
  | {
      kind: "filmGroup"
      heading: string
      key: string
      score: number
      films: BrowseFilmTileFragment[]
    }

export type SearchResults = {
  recommendations: RecommendationGroup[]
  tabs: SearchTab[]
  activeTab?: SearchTab
  results: SearchResult[]
  isLoading: boolean
  isFetching: boolean
}

export function useSearchResults(args: Args): SearchResults {
  const hydrating = useHydrating()
  const searchPatterns = useAppData((data) => data.templateParts?.searchPatterns?.contentBlocks)
  const programData = useProgramData().data

  const search = useSearch()
  const searchQuery = search.input.searchQuery

  const shouldShowSplash = !search.hasResults

  const tabs = useMemo(() => {
    const tabs: SearchTab[] = []
    const docs = search.documentResults ?? []

    tabs.push({
      key: "film",
      types: ["film", "strand"],
      label: "Films & Events",
      enabled: docs.some((doc) => doc.type === "film" || doc.type === "strand"),
    })

    tabs.push({
      key: "page",
      types: ["page", "venue"],
      label: "Pages",
      enabled: docs.some((doc) => doc.type === "page"),
    })

    tabs.push({
      key: "blog",
      types: ["blog"],
      label: "Articles",
      enabled: docs.some((doc) => doc.type === "blog"),
    })

    tabs.push({
      key: "guests",
      types: ["guest"],
      label: "Guests",
      enabled: docs.some((doc) => doc.type === "guest"),
    })

    return tabs
  }, [search.documentResults])

  const activeTab = tabs.find((tab) => tab.types?.some((type) => args.types?.includes(type)))

  const appData = useAppData()

  // Process recommended actions/searches
  const recommendations: RecommendationGroup[] = useMemo(() => {
    let recommendationResults =
      search.documentResults.filter((doc) => doc.type === "suggestedActions" || doc.type === "suggestedSearch") ?? []

    const groups = new Map<string, RecommendedLink[]>()
    const addRecommendation = (heading: string, item: RecommendedLink) => {
      if (!groups.has(heading)) {
        groups.set(heading, [])
      }
      groups.get(heading)?.push(item)
    }

    /**
     * Show admin-defined recommendations when the search is loading or when the query is empty.
     */
    if (shouldShowSplash) {
      for (const pattern of searchPatterns ?? []) {
        if (pattern.slug === "search/search-group") {
          for (const item of pattern.props.links ?? []) {
            addRecommendation(pattern.props.groupHeading!, {
              type: "link",
              label: item?.link?.title!,
              link: item?.link?.url!,
              external: item?.link?.target && item?.link?.target !== "_blank" ? true : false,
            })
          }
        } else if (pattern.slug === "search/suggested-search-group") {
          for (const item of pattern.props.items ?? []) {
            addRecommendation(pattern.props.groupHeading!, {
              type: "search",
              label: item?.searchTerm!,
              term: item?.searchTerm!,
            })
          }
        }
      }
    }
    /**
     * ... otherwise, show the recommendations from the API
     */
    if (!shouldShowSplash) {
      // Compile the indexed recommendations coming from the CMS via the search API
      for (const item of recommendationResults) {
        if (item.score < 0.3) continue
        if (item.type === "suggestedSearch") {
          addRecommendation(item.data.heading || "Suggested Searches", {
            type: "search",
            label: item.data.label,
            term: item.data.label,
          })
        } else if (item.type === "suggestedActions") {
          addRecommendation(item.data.heading || "Recommended Links", {
            type: "link",
            label: item.data.label,
            link: item.data.link,
            external: item.data.external,
          })
        }
      }

      // Compile some of the keywords into suggested searches
      const keywords =
        search.keywordResults
          .filter((keyword) => keyword.score > 0.2 && ["genreQuery", "countryQuery"].includes(keyword.type))
          .slice(0, 4) ?? []
      for (const keyword of keywords) {
        addRecommendation("Suggested Searches", {
          type: "search",
          label: keyword.label,
          term: keyword.label,
        })
      }
    }

    const recommendations: RecommendationGroup[] = Array.from(groups.entries()).map(([heading, items]) => ({
      heading,
      items,
    }))
    return recommendations
  }, [search.documentResults, search.keywordResults, shouldShowSplash])

  const results = useMemo(() => {
    if (hydrating) return []

    // Process documents & widgets
    let documents =
      search.documentResults.filter((doc) => !["suggestedActions", "suggestedSearch"].includes(doc.type)) ?? []

    if (search.results.intent.result?.tiles.length! > 0) {
      const filmIds = search.results.intent.result?.tiles.map((tile) => tile.filmId).filter(Boolean) ?? []
      documents = documents.filter((doc) => {
        return !(doc.type === "film" && filmIds.includes(doc.id))
      })
    }

    const widgets: SearchWidgetInstance[] = []

    if (activeTab) {
      documents = documents.filter((doc) => activeTab?.types?.includes(doc.type)) ?? []
    }

    for (const widgetType of Object.values(SEARCH_WIDGETS)) {
      const widget = widgetType.maybeGetWidget({
        documents,
        isMobile: !!args.isMobile,
        isQuickSearch: !!args.isQuickSearch,
        program: programData,
        types: args.types ?? [],
        appData: appData,
      })
      if (!widget) continue
      widgets.push(widget)
      // Filter out the documents that are already in the widget, avoiding duplication
      documents = documents.filter((doc) => {
        return !widget.presentingResults?.some((presentingResult) => {
          return presentingResult.id === doc.id && presentingResult.type === doc.type
        })
      })
    }

    // if (searchIntent.data) {
    //   widgets.push({
    //     key: "searchIntentDebug",
    //     node: <pre>{JSON.stringify(searchIntent.data, null, 2)}</pre>,
    //     placement: "center",
    //     score: 1,
    //     maximum: 0,
    //     presentingResults: [],
    //   })
    // }

    const results: SearchResult[] = shouldShowSplash
      ? []
      : [
          ...widgets.map(
            (widget): SearchResult => ({
              kind: "widget",
              widget,
              score: widget.score,
              key: widget.key + "_" + widget.placement,
            }),
          ),
          ...documents
            .map((item): SearchResult | null => {
              const node = getSimpleSearchResult({ item })!
              if (!node) return null
              return {
                kind: "document",
                score: item.score,
                key: "doc_" + item.id,
                document: item,
                node: node,
              }
            })
            .filter((item) => item !== null),
        ]

    if (shouldShowSplash) {
      const jumpBackIn = getLastViewedFilms()
        .map((item) => programData?.currentProgram?.films?.find((film) => film?.uri === item.uri))
        .filter((item) => !!item)
        .slice(0, 3)

      if (jumpBackIn.length >= 2 && Math.random() < 0.5) {
        results.push({
          kind: "filmGroup",
          heading: "Jump Back In",
          films: jumpBackIn,
          score: 0.3,
          key: "jumpBackIn",
        })
      } else if (programData?.topFilms && programData?.topFilms.length) {
        results.push({
          kind: "filmGroup",
          heading: "Trending Films",
          films: programData.topFilms.filter((film) => film !== null).slice(0, 2),
          score: 0.3,
          key: "topFilms",
        })
      }
    }

    results.sort((a, b) => {
      return b.score - a.score
    })

    if (args.isQuickSearch) {
      return results.slice(0, 5)
    }

    return results
  }, [
    search.documentResults,
    search.keywordResults,
    programData,
    activeTab,
    args.types,
    args.isMobile,
    shouldShowSplash,
    hydrating,
  ])

  return {
    tabs,
    activeTab,
    recommendations,
    results,
    isLoading: search.loading,
    isFetching: search.loading,
  }
}
